/**
 * A tool's path: /resources/<slug>.
 *
 * Replaces fifteen near-identical page components. They shared 2,460 lines of
 * inline style between them, rendered no site header or footer, and pulled in
 * two typefaces (DM Sans, Lora) and a teal (#2A9D8F) that exist nowhere else
 * on the site.
 *
 * Four changes of substance, not just of style:
 *
 *   1. A/B/C/D become 1/2/3. Letters read as alternatives, which is why these
 *      pages were places you visited once. Numbers read as a sequence you are
 *      partway through, which is what makes saved progress worth having.
 *   2. One click target per row. The old card made the whole row a link with
 *      a tag inside it; there was no way to tell where to click, and a control
 *      nested in a link is ambiguous for keyboard and screen reader users too.
 *      The row is a plain container now. Each step has exactly one button, on
 *      the right, labelled with what it does. Completion is a separate control
 *      that always carries a text label, never a bare checkbox.
 *   3. Locked steps render, when there are any. There are none right now —
 *      the eleven placeholders were removed on 26 Aug 2026 — but a step marked
 *      "soon" still draws as a greyed row with a notify control rather than
 *      being hidden, which is what you want for a guide that is genuinely
 *      about to land.
 *
 *   4. The last step on five of the paths is the paid companion app, and it is
 *      the only step whose content depends on who is looking. It used to be a
 *      bare outbound link for everyone, which meant a member who had already
 *      paid was shown the same thing as a stranger, and the stranger was sent
 *      to a sign-in wall with no idea what was behind it. Now: owners get a
 *      way in, everyone else gets the two ways to get it.
 *
 * Built mobile-first. Every interactive element clears 44px, and the action
 * moves below the text on a phone rather than being squeezed beside it. The
 * documented audience is women 55+ who do not consider themselves technical,
 * so target size and label clarity matter more here than density.
 */

import { useState } from "react";
import { BUTTON } from "@/lib/buttonStyles";
import { Link } from "wouter";
import { ArrowUpRight, Check, Lock, ChevronRight } from "lucide-react";
import BrandMark, { brandIdForProduct } from "@/components/BrandMark";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { useStack } from "@/hooks/useStack";
import { resources } from "@/lib/resourcesData";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/core/hooks/useAuth";
import { APP_BENEFITS, MEMBERSHIP, guideSteps, type AppOffer, type PathStep, type ToolPath } from "@/lib/ecosystemData";

/**
 * The two buttons on the navy app card.
 *
 * Not BUTTON.primarySm / secondarySm: both of those assume a light ground —
 * primary is navy-on-cream, which is invisible here, and secondary's border is
 * --border, a warm hairline that vanishes on navy. Same geometry as the shared
 * system (44px, rounded-md, 14px semibold), colours reversed.
 */
const APP_BUTTON = {
  filled:
    "inline-flex items-center justify-center gap-2 min-h-11 px-4 rounded-md text-sm font-semibold " +
    "bg-primary-foreground text-primary hover:opacity-90 transition-opacity " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
  outline:
    "inline-flex items-center justify-center gap-2 min-h-11 px-4 rounded-md text-sm font-semibold " +
    "border border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
} as const;

interface EcosystemPathProps {
  path: ToolPath;
}

export default function EcosystemPath({ path }: EcosystemPathProps) {
  const progress = useStack();
  /* Which companion apps this person already owns.
   *
   * Asked once here rather than inside the step, because there is at most one
   * app step on a path and a query per row would be a request per render for
   * an answer that does not vary by row.
   *
   * Not fetched while signed out: billing.myApps answers empty for an
   * anonymous caller by design, so the round trip has a known answer. That is
   * also why `ready` is tracked separately from the data. Treating "still
   * loading" as "does not own it" would show a paying member the buy buttons
   * for a beat before they resolved into the way in, and being sold something
   * you have already paid for is the one thing this step exists to stop. */
  const { isAuthenticated, loading: authLoading } = useAuth();
  const myApps = trpc.billing.myApps.useQuery(undefined, {
    enabled: !authLoading && isAuthenticated,
  });
  const ownedKeys = new Set((myApps.data?.apps ?? []).map(a => a.key));
  const ownershipKnown = !authLoading && (!isAuthenticated || myApps.isSuccess);

  const owns = (step: PathStep) =>
    Boolean(step.app && ownershipKnown && ownedKeys.has(step.app.productKey));

  /* Which grid this tool belongs to, and therefore where "back" goes. */
  const home = resources.some(r => r.id === path.slug)
    ? { href: "/resources", label: "Back to the Resource Center" }
    : { href: "/", label: "Back to the Roles Map" };

  /**
   * The bar counts every row on the page, app step included.
   *
   * It briefly did not, and that was worse: the app row is numbered 5 by its
   * own circle while the panel underneath read "you're on step 2 of 4", which
   * is a contradiction a reader can see in one glance and has no way to
   * resolve. Whatever the count means, it has to agree with the numbers
   * beside the rows.
   *
   *   Pending steps count. Counting only the live ones would show 100% to
   *   somebody who finished the single available step on a tool meant to have
   *   three, which overstates what they did and hides that more is coming.
   *
   *   The app step counts as DONE when they own it. That is the honest
   *   reading — owning the app is what finishing that row means — and it is
   *   what lets a path with an app reach 5 of 5 at all, since the row carries
   *   no "mark as done" to tick. Somebody who has worked through every guide
   *   but not bought the app sits at 4 of 5, which is exactly true: there is
   *   one more thing here, and it is the app.
   */
  const total = path.steps.length;
  /* Shared with the server, which fires the "finished this path" tag off the
     same set. Inlined here once; two copies of the filter would eventually
     disagree about somebody's last step, and the disagreement would show up as
     a progress bar at 5 of 5 with no tag, or a tag with the bar still short. */
  const steps = guideSteps(path);
  const appStep = path.steps.find(s => s.app);
  const done =
    progress.countComplete(steps.map(s => s.id)) +
    (appStep && owns(appStep) ? 1 : 0);
  const pct = total ? Math.round((done / total) * 100) : 0;

  /* The first unfinished guide. Drives the "up next" emphasis and the button
     at the bottom, and is never the app step — "go to step 5" against a
     purchase would send a non-owner to a sign-in wall, and the navy card
     already makes its own case far better than a generic button can. So the
     closing panel appears once the guides are done, whether or not the app
     has been bought. */
  const nextStep = steps.find(s => !progress.isStepComplete(s.id));
  const pendingCount = path.steps.filter(s => s.state === "soon").length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* The way back was a link in the body, at the very top of the content,
          which on a five-step path meant scrolling back up to leave. It is the
          same link; it is now in the header, where it stays put.

          Where it goes depends on which grid the entry is in. Canva,
          HighLevel and Open.Video are Resource Center entries and were sending
          people to the Roles Map, which is not where they came from and does
          not contain what they were looking at. Derived from `resources`
          rather than listed here, because an entry moving between the two
          grids is a thing that actually happens — Canva moved in August — and
          a hand-kept list would have been wrong from that day. */}
      <SiteHeader backHref={home.href} backLabel={home.label} />

      <main className="flex-1">
        <div className="container max-w-3xl py-8 sm:py-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Your {path.name} path
          </p>

          <h1 className="mt-3 font-['Fraunces'] text-3xl sm:text-4xl font-semibold text-foreground leading-tight tracking-tight">
            {path.headline}
            {path.headlineAlt ? (
              <span className="block italic text-primary/80">{path.headlineAlt}</span>
            ) : null}
          </h1>

          <ProgressBar done={done} total={total} pct={pct} pending={pendingCount} />

          <ol className="mt-6 space-y-3">
            {path.steps.map((step, i) =>
              step.app ? (
                <AppStep
                  key={step.id}
                  step={step}
                  offer={step.app}
                  index={i + 1}
                  owned={owns(step)}
                  ownershipKnown={ownershipKnown}
                />
              ) : (
                <StepRow
                  key={step.id}
                  step={step}
                  index={i + 1}
                  isNext={nextStep?.id === step.id}
                  toolSlug={path.slug}
                  progress={progress}
                />
              )
            )}
          </ol>

          {nextStep ? (
            <section className="mt-10 rounded-xl border border-border border-l-4 border-l-primary bg-card p-6">
              {/* "You're on step 2 of 4", not "you're 1 of 4 through".
                  The old phrasing counted what was behind you, which reads as
                  a score; this names where you are, which is the same fact
                  pointed forwards and matches the button underneath it.

                  The number is the step's position in the path, so it agrees
                  with the numbered circle on the row and with "Go to step 2".
                  That works out equal to its position among counted steps
                  because the app step, the only uncounted one, is always
                  last — if that ever stops being true these two diverge. */}
              <h2 className="font-['Fraunces'] text-xl font-semibold text-foreground">
                You&rsquo;re on step {path.steps.indexOf(nextStep) + 1} of {total}.
              </h2>
              <p className="mt-2 text-base text-muted-foreground leading-relaxed">
                {done === 0
                  ? total === 1
                    /* No guides to come back for. Four paths are a single
                       outbound link until someone writes their second step. */
                    ? "Open the tool and have a look around. Guides for this one are on the way."
                    : "Most people try the tool first, then come back for the guides."
                  : `Next up: ${nextStep.title.replace(/^I( a|')m ready to |^I want to /i, "")}`}
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <StepAction step={nextStep} variant="primary" label={`Go to step ${path.steps.indexOf(nextStep) + 1}`} />
                <Link
                  href="/"
                  className="inline-flex items-center justify-center min-h-11 px-5 rounded-md border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                >
                  Browse all tools
                </Link>
              </div>
            </section>
          ) : (
            /* Every live step is done. If steps are still being written we say
               so rather than declaring the path finished, which would be both
               untrue and a reason never to come back. */
            <section className="mt-10 rounded-xl border border-border border-l-4 border-l-primary bg-card p-6">
              <h2 className="font-['Fraunces'] text-xl font-semibold text-foreground">
                {pendingCount > 0
                  ? "You're up to date on this one."
                  : `${path.name} is set up properly.`}
              </h2>
              <p className="mt-2 text-base text-muted-foreground leading-relaxed">
                {pendingCount > 0
                  ? `${pendingCount === 1 ? "One more step is" : `${pendingCount} more steps are`} being written. Ask to be told when ${pendingCount === 1 ? "it lands" : "they land"}, or find the next tool your business needs.`
                  : "That is the whole path. Find the next tool your business needs."}
              </p>
              <div className="mt-5">
                <Link
                  href="/"
                  className={BUTTON.primarySm}
                >
                  Run the Roles Map again
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </section>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function ProgressBar({
  done,
  total,
  pct,
  pending,
}: {
  done: number;
  total: number;
  pct: number;
  /** Steps counted in the total that have no guide written yet. */
  pending: number;
}) {
  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {/* "1 steps, in order" was fine while every path had at least
                three. Removing the locked placeholders left four paths with a
                single row, and the plural showed up immediately. */}
            {total === 1 ? "One step" : `${total} steps, in order`}
          </p>
          <p className="text-sm text-muted-foreground">
            {pending > 0
              ? `Check them off as you go. ${pending} ${pending === 1 ? "step is" : "steps are"} still being written.`
              : total === 1
                ? "Tick it off when you have had a look. Your progress saves automatically."
                : "Check them off as you go. Your progress saves automatically."}
          </p>
        </div>
        <div className="flex items-center gap-3 sm:ml-auto sm:min-w-[190px]">
          <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-sm font-semibold text-primary whitespace-nowrap">
            {done} of {total}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The single action for a step. This is the only thing in a row that looks
 * like a button, which is the whole point.
 */
function StepAction({
  step,
  variant,
  label,
}: {
  step: PathStep;
  variant: "primary" | "secondary";
  label?: string;
}) {
  // The step button is the most-clicked control on the site: fifty-two of
  // them across fifteen paths. It uses the shared system so it cannot drift.
  const cls = `whitespace-nowrap ${
    variant === "primary" ? BUTTON.primarySm : BUTTON.secondarySm
  }`;

  const text = label ?? (step.external ? `Open ${step.tag.replace(/^Visit /, "")}` : step.tag);

  if (step.external) {
    return (
      <a href={step.href} target="_blank" rel="noopener noreferrer" className={cls}>
        {text}
        <ArrowUpRight className="w-4 h-4" />
      </a>
    );
  }
  return (
    <Link href={step.href} className={cls}>
      {text}
      <ChevronRight className="w-4 h-4" />
    </Link>
  );
}

/**
 * The companion-app step: the last row on the five paths that sell one.
 *
 * Deliberately a different object from every other row on the page. The rest
 * of the path is free and reads as a checklist; this one costs $247 or a
 * subscription, and dressing it as a sixth checklist item was the problem —
 * it scrolled past as one more thing to tick rather than the thing the path
 * has been building toward. So it is the site's navy, reversed out in cream,
 * with the three reasons stated rather than implied.
 *
 * No completion control. You do not "finish" an app you have bought, and a
 * checkbox next to a price invites the reading that ticking it is the
 * purchase. EcosystemPath leaves app steps out of the progress count for the
 * same reason.
 */
function AppStep({
  step,
  offer,
  index,
  owned,
  ownershipKnown,
}: {
  step: PathStep;
  offer: AppOffer;
  index: number;
  /** This person is entitled to it right now. */
  owned: boolean;
  /** False while the entitlement answer is still in flight. */
  ownershipKnown: boolean;
}) {
  return (
    <li className="rounded-xl border border-primary bg-primary text-primary-foreground p-5 sm:p-6 shadow-sm">
      <div className="flex gap-4">
        <span
          aria-hidden="true"
          className="shrink-0 w-9 h-9 rounded-full grid place-items-center font-['Fraunces'] text-sm font-bold border border-primary-foreground/30 bg-primary-foreground/10"
        >
          {index}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-base sm:text-lg font-semibold leading-snug">{step.title}</p>
          <p className="mt-0.5 text-sm text-primary-foreground/70 leading-snug">{step.sub}</p>

          {/* The app's own mark, so the card is identifiable at a glance and
              matches how the same app appears in the storefront. */}
          <div className="mt-4 flex items-center gap-3">
            <BrandMark brandId={brandIdForProduct(offer.productKey)} tone="onNavy" />
            <p className="font-['Fraunces'] text-lg font-semibold">{offer.name}</p>
          </div>

          {owned ? (
            <p className="mt-3 inline-flex items-start gap-2 text-sm font-semibold">
              <Check className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              You have this. Click the button below to open the app, already
              signed in.
            </p>
          ) : (
            <>
              {/* What this app is for, then what every companion app gives
                  you. The first sentence is the only part that differs
                  between the five, so it leads. */}
              <p className="mt-3 text-sm text-primary-foreground/90 leading-relaxed">
                {offer.blurb}
              </p>
              <ul className="mt-3 space-y-1.5">
                {APP_BENEFITS.map(benefit => (
                  <li key={benefit} className="flex items-start gap-2.5 text-sm leading-relaxed">
                    <Check
                      className="w-4 h-4 mt-0.5 shrink-0 text-primary-foreground/70"
                      aria-hidden="true"
                    />
                    <span className="text-primary-foreground/90">{benefit}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Reversed out of the navy: the filled button is cream on navy rather
          than navy on cream, and the outline is the same shape in a hairline.
          The prices are on the buttons rather than in a line above them, so
          the choice and its cost are the same glance. */}
      <div className="mt-5 sm:pl-13 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        {owned ? (
          <a
            href={step.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`${APP_BUTTON.filled} sm:w-auto`}
          >
            Open {offer.name}
            <ArrowUpRight className="w-4 h-4" />
          </a>
        ) : ownershipKnown ? (
          <>
            <a
              href={offer.checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={APP_BUTTON.filled}
            >
              Get it for {offer.price}
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href={MEMBERSHIP.href}
              target="_blank"
              rel="noopener noreferrer"
              className={APP_BUTTON.outline}
            >
              Or {MEMBERSHIP.name} for {MEMBERSHIP.price}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </>
        ) : (
          /* Ownership still in flight. Render the way in rather than the
             prices: showing a member the buy buttons for a beat, even briefly,
             is the one outcome this step exists to prevent. */
          <a
            href={step.href}
            target="_blank"
            rel="noopener noreferrer"
            className={APP_BUTTON.filled}
          >
            Open {offer.name}
            <ArrowUpRight className="w-4 h-4" />
          </a>
        )}
      </div>
    </li>
  );
}

function StepRow({
  step,
  index,
  isNext,
  toolSlug,
  progress,
}: {
  step: PathStep;
  index: number;
  isNext: boolean;
  /** Stored alongside the step so My Stack can group without parsing ids. */
  toolSlug: string;
  progress: ReturnType<typeof useStack>;
}) {
  const [notified, setNotified] = useState(false);
  const locked = step.state === "soon";
  const complete = progress.isStepComplete(step.id);
  const on = progress.completedOn(step.id);

  const shell = complete
    ? "border-primary/30 bg-primary/5"
    : isNext
      ? "border-primary border-2 shadow-sm"
      : locked
        ? "border-border bg-muted/30"
        : "border-border bg-card";

  /* The completion control. It sits at the bottom right of the box, opposite
     the action, so the row reads left to right as "here is the thing to do,
     here is how you say you did it" — and so that the thing you are meant to
     click first is the one nearest the text that describes it. It always
     carries a text label; a bare box on the edge of a row reads as the primary
     control no matter how it is styled. */
  const completion = locked ? null : complete ? (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm sm:justify-end">
      <span className="inline-flex items-center gap-1.5 font-semibold text-primary">
        <Check className="w-4 h-4" /> Done{on ? ` ${on}` : ""}
      </span>
      <button
        onClick={() => progress.setStepComplete(step.id, toolSlug, !complete)}
        className="underline underline-offset-2 text-muted-foreground hover:text-foreground"
      >
        Undo
      </button>
    </p>
  ) : (
    <button
      onClick={() => progress.setStepComplete(step.id, toolSlug, !complete)}
      className="inline-flex items-center gap-2 min-h-11 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
    >
      <span className="w-[18px] h-[18px] rounded border-[1.5px] border-border grid place-items-center shrink-0" />
      Mark as done
    </button>
  );

  return (
    <li
      className={`rounded-xl border p-4 sm:p-5 ${shell} ${locked ? "opacity-80" : ""}`}
    >
      <div className="flex gap-4">
        <span
          aria-hidden="true"
          className={`shrink-0 w-9 h-9 rounded-full grid place-items-center font-['Fraunces'] text-sm font-bold border ${
            complete
              ? "bg-primary text-primary-foreground border-primary"
              : isNext
                ? "bg-primary/10 text-primary border-primary"
                : "bg-muted text-muted-foreground border-border"
          }`}
        >
          {complete ? <Check className="w-4 h-4" /> : locked ? <Lock className="w-3.5 h-3.5" /> : index}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-foreground leading-snug">
            {step.title}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground leading-snug">{step.sub}</p>

        </div>
      </div>

      {/* Action bottom left, completion bottom right. Both stack on a phone so
          the action gets full width rather than being squeezed against the
          edge, and the completion control keeps its own 44px row. */}
      <div className="mt-4 sm:mt-3 sm:pl-13 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          {locked ? (
            <button
              onClick={() => setNotified(true)}
              disabled={notified}
              className="inline-flex items-center justify-center gap-2 min-h-11 px-5 rounded-md border border-border text-sm font-semibold text-muted-foreground hover:bg-muted disabled:opacity-70 transition-colors"
            >
              {notified ? (
                <>
                  <Check className="w-4 h-4" /> We&rsquo;ll let you know
                </>
              ) : (
                "Tell me when this is ready"
              )}
            </button>
          ) : (
            <StepAction
              step={step}
              variant={isNext || complete ? (complete ? "secondary" : "primary") : "secondary"}
            />
          )}
        </div>

        {completion ? <div className="sm:ml-auto">{completion}</div> : null}
      </div>
    </li>
  );
}
